import { ChatBlockMounts } from "./block-mounts";
import { contentBlockAt } from "./content-blocks";
import { html, LonaWebComponent } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { css, Style } from "@tento-ui/component-styles";
import { DomUtils } from "@tento-ui/dom";
import type { ChatPluginManager } from "./chat-block-plugin";
import { CodeBlockPlugin } from "./chat-block-plugin";
import type { ChatTurn } from "./chat-turn";
import type { ChatMessage } from "./chat-types";

// A turn in the terminal register: the blocks, and nothing else.
//
// The bubble's chrome — role alignment, the tinted ground, the max-width, the
// latency caption — is the CHAT surface's presentation of a turn. A terminal
// has none of it: in a TUI every turn is rows in one column of one screen,
// the prompt drawn with the same cells as the output, and which turn a row
// belongs to is carried by the rows themselves (the composer glyph, the
// bullet), not by a box around them. So this element renders a message's
// blocks stacked full-width and declares no colour, face, or inset of its
// own — the PANE around the thread owns the ground and the gutter, exactly
// as a terminal window does.
//
// Everything mechanical is the bubble's mechanics, kept identical on
// purpose: the same plugin loop, the same eager-then-observed height
// reporting, the same recycle discipline. The thread cannot tell the two
// apart — which is the proof the `ChatTurn` seam is real.
@component({ name: "std-chat-term-turn" })
export class ChatTermTurn extends LonaWebComponent implements ChatTurn {
  private $content: HTMLElement = this.$("content");

  private mounts = new ChatBlockMounts();
  private currentIndex: Option<number> = null;
  private currentMessageId: Option<string> = null;
  private resizeObserver: Option<ResizeObserver> = null;
  private lastHeight: number = 0;
  // Every bind needs a valid report, including a new message at the same
  // pooled index while geometry is absent. Keep the last extent until then.
  private measuredIndex: Option<number> = null;

  onHeightMeasured: Option<(index: number, height: number) => void> = null;
  onMessageClick: Option<(messageId: string) => void> = null;

  connectedCallback(): void {
    super.connectedCallback();
    this.resizeObserver = new ResizeObserver((entries) => {
      // A temporarily absent content box still wraps terminal text into a
      // large, positive height. That is not a usable virtualizer extent.
      // Check the observed content width as well as the current box: an
      // observation queued before another layout change can already be stale.
      const entry = entries.find((entry) => entry.target === this.$content);
      if (!entry || !(entry.contentRect.width > 0) || !this.hasContentWidth()) return;
      // offsetHeight, never contentRect: the turn gap is PADDING on
      // #content, and contentRect excludes padding — measuring it would
      // report the gapless height and collapse the blank line the look
      // asked for (and shrink the user band sized against the host).
      const height = this.$content.offsetHeight;
      if (height > 0 && this.currentIndex != null &&
          (height !== this.lastHeight || this.currentIndex !== this.measuredIndex)) {
        this.lastHeight = height;
        this.measuredIndex = this.currentIndex;
        this.style.height = height + "px";
        this.onHeightMeasured?.(this.currentIndex, height);
      }
    });
    this.resizeObserver.observe(this.$content);

    if (this.currentIndex != null && this.lastHeight === 0) {
      this.measureNow();
    }
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
  }

  bind(message: ChatMessage, manager: ChatPluginManager, index: number): void {
    this.currentIndex = index;
    this.currentMessageId = message.id;
    this.measuredIndex = null;
    // The role on the host is the theme sheet's per-role handle: a look
    // bands or dims its own turn element by attribute, without piercing any
    // shadow root.
    this.toggleAttribute("user", message.role === "user");
    this.toggleAttribute("assistant", message.role !== "user");

    this.mounts.clear();
    DomUtils.clearChildren(this.$content);

    for (const [blockIndex, block] of Array.from(message.content, contentBlockAt).entries()) {
      const plugin = manager.resolve(block.type, this.pluginTypeFor(block.type, message));
      const $block = this.mounts.mount(plugin, block, message.isStreaming, {
        key: JSON.stringify([message.id, block.id ?? blockIndex]), disclosure: manager.disclosure,
      });
      this.$content.appendChild($block);
    }

    this.measureNow();
  }

  /**
   * Which plugin renders a block. The register takes every block at its
   * WORD — no markdown upgrade, because a turn's text is monospace lines
   * whichever side wrote it, and no dialect, because a terminal register is
   * a mechanism rather than a house style.
   *
   * This is the seam a dialect overrides, and the only thing a look needs in
   * order to draw turns its own way: `ChatCodexTurn` routes onto the codex
   * grammar, `ChatClaudeTurn` onto Claude Code's, and each ships beside the
   * look that declares it. The method used to return `*_tui` here, which
   * meant every terminal look inherited the Codex CLI's bullets whether or
   * not it wanted them — including looks written specifically not to look
   * like codex.
   */
  protected pluginTypeFor(blockType: string, _message: ChatMessage): string {
    return blockType;
  }

  private measureNow(): void {
    if (!this.hasContentWidth()) return;
    const height = this.$content.offsetHeight;
    if (height > 0) {
      this.lastHeight = height;
      this.measuredIndex = this.currentIndex;
      this.style.height = height + "px";
      if (this.currentIndex != null) {
        this.onHeightMeasured?.(this.currentIndex, height);
      }
    }
  }

  private hasContentWidth(): boolean {
    // clientWidth rounds to whole pixels, so fractional padding can make a
    // zero-width content box appear positive. The computed used width keeps
    // the content-box precision; border-box width includes its own insets.
    const style = getComputedStyle(this.$content);
    let width = parseFloat(style.width);
    if (style.boxSizing === "border-box") {
      width -= parseFloat(style.paddingLeft) + parseFloat(style.paddingRight) +
        parseFloat(style.borderLeftWidth) + parseFloat(style.borderRightWidth);
    }
    return Number.isFinite(width) && width > 0;
  }

  appendDelta(blockIndex: number, delta: string): void {
    this.mounts.appendDelta(blockIndex, delta);
  }

  finalizeBlock(blockIndex: number): void {
    this.mounts.finalize(blockIndex);
  }

  recycle(): void {
    this.mounts.clear();
    DomUtils.clearChildren(this.$content);
    this.lastHeight = 0;
    this.measuredIndex = null;
    this.currentIndex = null;
    this.currentMessageId = null;
    this.onHeightMeasured = null;
    this.onMessageClick = null;
    this.toggleAttribute("user", false);
    this.toggleAttribute("assistant", false);
  }

  onRecycle(): void {
    super.onRecycle();
    this.recycle();
  }

  static $styles: Style.Compat[] = [
    CodeBlockPlugin.$panelStyle,
    css`
      :host {
        display: block;
        width: 100%;
        box-sizing: border-box;
        overflow: hidden;
      }

      /* The thread's boundaries debug affordance — every register carries
         it, because a virtualizer bug looks the same in all of them. */
      :host([debug-boundaries]) {
        outline: 1px solid var(--red, red);
        outline-offset: -1px;
      }

      /* Absolute for the same reason the bubble's wrapper is: the host's
         height is the virtualizer's coordinate, set explicitly, while the
         content renders at its natural height and reports it. The slotted
         cell's transform makes the host the containing block.

         The font line is the terminal's one metric: a TUI has exactly one
         face, one size, one leading — the cell grid — and a turn's prose is
         rows too. These are the SAME hooks the terminal block paints with
         (falling back to inheritance exactly as its inline vars do), so a
         pane sets --chat-terminal-{font,size,leading} once and text and
         terminal blocks cannot disagree. */
      #content {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        font-family: var(--chat-terminal-font, inherit);
        font-size: var(--chat-terminal-size, inherit);
        line-height: var(--chat-terminal-leading, inherit);
        /* The row's own gutter. The thread never pads horizontally — each
           turn insets itself — so a full-width affordance (codex's user
           band on the HOST) bleeds to the edge while the rows inside keep
           the terminal's gutter, exactly as a real TUI paints a band. */
        padding-inline: var(--chat-turn-inset, 0px);
        /* A CLI breathes in blank lines: --chat-tui-block-gap opens air
           between a turn's items and --chat-tui-turn-gap after the turn,
           both 0 by default (the tsh figures are gapless by construction).
           They are measured — padding and flex gap land in the height the
           turn reports — so a look states them in lh and the grid holds. */
        display: flex;
        flex-direction: column;
        gap: var(--chat-tui-block-gap, 0px);
        padding-block-end: var(--chat-tui-turn-gap, 0px);
      }

      /* A banded prompt can carry blank lines INSIDE the band — codex
         paints one above and one below the user's text. The pad joins the
         turn gap on the end side, and because a look's band gradient is
         sized minus the turn gap alone, the pad stays banded while the
         gap after the band stays ground. 0 by default, measured like the
         gaps, so only a look that states it pays for it. */
      :host([user]) #content {
        padding-block-start: var(--chat-tui-prompt-pad, 0px);
        padding-block-end: calc(
          var(--chat-tui-prompt-pad, 0px) + var(--chat-tui-turn-gap, 0px)
        );
      }

      /* The base typography sheet — adopted into this shadow root ahead of
         these styles — sizes every text tag at the product's prose scale
         (cap-height math, trim margins, its own min-height). In a terminal
         register that scale does not exist: every text primitive a block
         plugin can mint stands down to the inherited cell metric, holding
         the same 1lh floor a blank terminal row holds. */
      #content :is(h1, h2, h3, h4, h5, h6, h7, p, ul, ol, caption, label) {
        font: inherit;
        margin: 0;
        padding: 0;
        min-height: 1lh;
      }
    `,
  ];

  static $html: Option<HTMLTemplateElement> = html`
    <div id="content"></div>
  `;
}
